import React, {useEffect, useImperativeHandle, useRef, useState, useMemo} from 'react';
import {style, classes} from './google-map-embedded.st.css';
import {LocationCoordinates, Values, htmlSameOriginIframeCreateMarking, languageIsRtl} from "@wix/devzai-utils-common";
import {cssOverlay} from "../css-common/css-common";
import {GoogleMapEmbeddedBootstrapOptions, googleMapEmbeddedBootstrap} from './google-map-embedded-bootstrap';
import {useEventCallback} from "../hooks";

export const MapInteractionMode = {
    OnlyAttributionInteractive: 'OnlyAttributionInteractive',
    NoInteraction: 'NoInteraction',
    FullInteraction: 'FullInteraction',
} as const;

export type MapInteractionMode = Values<typeof MapInteractionMode>;

export const GoogleMapEmbedded = React.memo(function GoogleMapEmbedded (props: GoogleMapEmbedded.Props) {
    const {
        locationCoordinates,
        languageCode = 'en',
        className,
        zoom,
        googleMapsApiKey,
        interactionMode = MapInteractionMode.OnlyAttributionInteractive,
        controllerRef,
        onClick,
        onMapReady,
        isDarkMode,
        ...htmlAttributes
    } = props;

    const [contentWindow, setContentWindow] = useState<
        Window & {updateMapProps?: (mapProps: Partial<GoogleMapEmbeddedBootstrapOptions>) => void} | null
    >(null);
    const [mapReady, setMapReady] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(controllerRef, () => ({}));

    useEffect(() => {
        const cleanupFunction = (contentWindow && googleMapsApiKey) ? googleMapEmbeddedBootstrap(
            contentWindow, {
                locationCoordinates: locationCoordinates,
                zoom: zoom,
                isDarkMode: isDarkMode,
                languageCode: languageCode,
                googleMapsApiKey: googleMapsApiKey,
                disableMapInteractions: (interactionMode !== MapInteractionMode.FullInteraction),
                onMapReady: () => {
                    setMapReady(true);
                    onMapReady?.();
                }
            }
        ) : null;

        return () => {
            cleanupFunction?.();
            setMapReady(false);
        };
    }, [
        contentWindow, googleMapsApiKey, languageCode
        // rest of the props change should only trigger updateMapProps
    ]);

    useEffect(() => {
        if (mapReady) {
            contentWindow?.updateMapProps?.({
                locationCoordinates: locationCoordinates,
                zoom: zoom,
                isDarkMode: isDarkMode,
                disableMapInteractions: (interactionMode !== MapInteractionMode.FullInteraction),
            });
        }
    }, [mapReady, locationCoordinates, zoom, interactionMode, isDarkMode]);

    const handleOverlayClick = useEventCallback((event: React.MouseEvent) => {
        onClick?.(event);
    });

    const blobObjectUrl = useMemo(() => {
        const htmlContent = `<html lang="${languageCode}" dir="${languageIsRtl(languageCode as Parameters<typeof languageIsRtl>[0]) ? 'rtl' : 'ltr'}"><head></head><body></body></html>`;
        const blob = new Blob([htmlContent], { type: 'text/html' });
        return URL.createObjectURL(blob);
    }, [languageCode]);

    useEffect(() => {
        return () => {
            if (blobObjectUrl) {
                URL.revokeObjectURL(blobObjectUrl);
            }
        };
    }, [blobObjectUrl]);

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            <iframe
                ref={iframeRef}
                {...htmlSameOriginIframeCreateMarking()}
                className={classes.mapContainer}
                src={blobObjectUrl}
                onLoad={(event) => {
                    setContentWindow((event.target as HTMLIFrameElement).contentWindow);
                }}
                title={`Map view at ${locationCoordinates.latitude},${locationCoordinates.longitude}`}
                tabIndex={-1}
            />
            {interactionMode !== MapInteractionMode.FullInteraction && (
                <div 
                    className={style(
                        classes.overlay,
                        {isAttributionInteractive: interactionMode === MapInteractionMode.OnlyAttributionInteractive},
                        cssOverlay()
                    )}
                    onClick={handleOverlayClick}
                />
            )}
        </div>
    );
});

export interface GoogleMapEmbedded {
}

export namespace GoogleMapEmbedded {

    export interface Props extends React.HTMLAttributes<any> {
        controllerRef?: React.Ref<GoogleMapEmbedded>,
        locationCoordinates: LocationCoordinates;
        zoom: number;
        languageCode?: string;
        googleMapsApiKey: string;
        interactionMode?: MapInteractionMode;
        onMapReady?: () => void;
        isDarkMode?: boolean;
    }
}
